import React from "react";
import { Button } from "./ui/button";
import { Lightbulb, Plus, X, Loader2, Zap, Wrench, BadgeCheck, LayoutTemplate, BarChart3, ListChecks } from "lucide-react";

const CATEGORIES = {
  skills: { label: "Skills to claim", icon: ListChecks, hint: "Named skills the job asks for" },
  evidence: { label: "Evidence to add", icon: BarChart3, hint: "Proof of scope, scale or outcome" },
  tooling: { label: "Tools & platforms", icon: Wrench, hint: "Specific tools named in the posting" },
  certification: { label: "Certifications", icon: BadgeCheck, hint: "Formal credentials the employer expects" },
  formatting: { label: "Formatting & structure", icon: LayoutTemplate, hint: "How the resume reads and parses" },
};

const DIMENSIONS = {
  role_alignment: "Role Alignment",
  evidence_strength: "Evidence Strength",
  content_completeness: "Content Completeness",
  clarity: "Clarity",
  ats_readability: "ATS Readability",
};

const categoryOf = (item) => (CATEGORIES[item.category] ? item.category : "skills");

export const SuggestionTray = ({ keywords, skills, additions, actions, dismissed, applying, batchProgress, onDismiss, onKeyword, onInstruction, onBatch }) => {
  const [selected, setSelected] = React.useState([]);
  const toggle = (key) => setSelected(s => s.includes(key) ? s.filter(k => k !== key) : [...s, key]);
  // Applied items are dismissed; keeping them ticked would let the same change be sent twice.
  React.useEffect(() => { setSelected(s => s.filter(k => !dismissed.includes(k))); }, [dismissed]);
  const have = new Set((skills || []).map(s => s.toLowerCase()));
  const terms = (keywords || []).filter(k => !dismissed.includes(`kw:${k}`) && !have.has(k.toLowerCase()));
  const items = (additions || []).filter((_, i) => !dismissed.includes(`add:${i}`));
  const todos = (actions || []).filter((_, i) => !dismissed.includes(`act:${i}`));
  if (!terms.length && !items.length && !todos.length) return null;

  const grouped = items.reduce((acc, item) => {
    const key = categoryOf(item);
    acc[key] = acc[key] || [];
    acc[key].push(item);
    return acc;
  }, {});

  const selectable = [
    ...items.map(item => `add:${(additions || []).indexOf(item)}`),
    ...todos.map(action => `act:${(actions || []).indexOf(action)}`),
  ];
  const labelFor = (key) => {
    const [kind, idx] = key.split(":");
    if (kind === "add") {
      const item = (additions || [])[Number(idx)];
      return item ? `${item.item}. Context: ${item.why}` : "";
    }
    return (actions || [])[Number(idx)] || "";
  };

  return (
    <div className="space-y-4" data-testid="suggestion-tray">
      {(!!items.length || !!todos.length) && (
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid="one-tap-polish">
          <div>
            <p className="text-sm font-semibold text-slate-900">One-tap polish</p>
            <p className="text-xs text-slate-500 mt-0.5">Tick everything that's genuinely true for you, then apply it all in one pass. Undo stays available.</p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
            <Button variant="outline" onClick={() => setSelected(selectable)} disabled={!!applying || selected.length === selectable.length} className="h-10 border-slate-300 text-slate-700 text-xs" data-testid="select-all-suggestions">Select all ({selectable.length})</Button>
            <Button onClick={async () => { const keys = selected; await onBatch(keys.map(labelFor).filter(Boolean), keys); setSelected([]); }} disabled={!!applying || !selected.length} className="h-10 bg-emerald-600 hover:bg-emerald-500 text-white text-xs" data-testid="apply-selected-suggestions">
              {applying === "batch" ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> {batchProgress?.total > 1 ? `Applying… (${batchProgress.done}/${batchProgress.total})` : "Applying…"}</> : `Apply ${selected.length || ""} selected`.trim()}
            </Button>
          </div>
        </div>
      )}
      {!!terms.length && (
        <div className="rounded-xl border border-amber-200 bg-white p-4" data-testid="generated-missing-keywords">
          <p className="text-xs font-mono uppercase tracking-wider text-amber-600 mb-1">Terms to review — add only if true</p>
          <p className="text-[11px] text-slate-500 mb-3">Add puts the term straight into Core Skills. Remove drops it from this list.</p>
          <ul className="flex flex-wrap gap-2">
            {terms.slice(0, 18).map((term) => (
              <li key={term} className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-50 pl-3 pr-1 py-1" data-testid={`keyword-chip-${term}`}>
                <span className="text-xs text-slate-700">{term}</span>
                <button onClick={() => onKeyword(term, false)} disabled={!!applying} title={`Add ${term} to Core Skills`} className="p-1 rounded-full text-emerald-700 hover:bg-emerald-50 disabled:opacity-50" data-testid={`keyword-add-${term}`}>
                  {applying === `kw:${term}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => onDismiss(`kw:${term}`)} title={`Remove ${term}`} className="p-1 rounded-full text-slate-500 hover:text-rose-600 hover:bg-rose-50" data-testid={`keyword-remove-${term}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!!items.length && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="suggested-additions-tray">
          <p className="text-xs font-mono uppercase tracking-wider text-amber-600 mb-1 flex items-center gap-1.5"><Lightbulb className="w-3.5 h-3.5" /> Confirm before adding</p>
          <p className="text-[11px] text-slate-500 mb-3">Not in your resume yet. Add only if genuinely true — we never fabricate. Each one shows the Resume Health score it lifts.</p>
          <div className="space-y-4">
            {Object.entries(grouped).map(([key, group]) => {
              const Icon = CATEGORIES[key].icon;
              return (
                <section key={key} data-testid={`suggestion-category-${key}`}>
                  <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-500">
                    <Icon className="w-3.5 h-3.5 text-amber-700" /> {CATEGORIES[key].label}
                    <span className="hidden sm:inline text-slate-500">· {CATEGORIES[key].hint}</span>
                  </p>
                  <ul className="mt-2 space-y-3">
                    {group.map((s) => {
                      const index = items.indexOf(s);
                      const key2 = `add:${(additions || []).indexOf(s)}`;
                      return (
                        <li key={index} className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`suggestion-item-${(additions || []).indexOf(s)}`}>
                          <label className="flex items-start gap-2">
                            <input type="checkbox" className="mt-1 w-4 h-4 shrink-0" checked={selected.includes(key2)} onChange={() => toggle(key2)} data-testid={`suggestion-select-${(additions || []).indexOf(s)}`} />
                            <span className="text-sm font-medium text-slate-900 break-words">{s.item}</span>
                          </label>
                          <p className="mt-1 text-xs text-slate-500">{s.why}</p>
                          {DIMENSIONS[s.dimension] && (
                            <p className="mt-2 inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] text-sky-700" data-testid={`suggestion-dimension-${(additions || []).indexOf(s)}`}>
                              Lifts {DIMENSIONS[s.dimension]}
                            </p>
                          )}
                          <div className="mt-3 grid grid-cols-2 gap-2 sm:flex">
                            <Button onClick={() => onInstruction(key2, `${s.item}. Context: ${s.why}`)} disabled={!!applying} className="h-10 bg-emerald-600 hover:bg-emerald-500 text-white text-xs" data-testid={`suggestion-add-${(additions || []).indexOf(s)}`}>
                              {applying === key2 ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Adding…</> : <><Plus className="w-3.5 h-3.5 mr-1.5" /> Add to resume</>}
                            </Button>
                            <Button onClick={() => onDismiss(key2)} variant="outline" className="h-10 border-slate-300 text-slate-500 hover:text-rose-600 text-xs" data-testid={`suggestion-dismiss-${(additions || []).indexOf(s)}`}>
                              <X className="w-3.5 h-3.5 mr-1.5" /> Not true for me
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })}
          </div>
        </div>
      )}

      {!!todos.length && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-4" data-testid="action-suggestions-tray">
          <p className="text-xs font-mono uppercase tracking-wider text-sky-700 mb-1 flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-amber-600" /> Immediate actions from your analysis</p>
          <p className="text-[11px] text-slate-500 mb-3">Apply rewrites only the affected lines of this resume. Undo is always available.</p>
          <ul className="space-y-3">
            {todos.map((action) => {
              const key = `act:${(actions || []).indexOf(action)}`;
              return (
                <li key={key} className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`action-suggestion-${(actions || []).indexOf(action)}`}>
                  <label className="flex items-start gap-2">
                    <input type="checkbox" className="mt-1 w-4 h-4 shrink-0" checked={selected.includes(key)} onChange={() => toggle(key)} data-testid={`action-select-${(actions || []).indexOf(action)}`} />
                    <span className="text-sm text-slate-800 break-words">{action}</span>
                  </label>
                  <div className="mt-3 grid grid-cols-2 gap-2 sm:flex">
                    <Button onClick={() => onInstruction(key, action)} disabled={!!applying} className="h-10 bg-sky-600 hover:bg-sky-500 text-white text-xs" data-testid={`action-apply-${(actions || []).indexOf(action)}`}>
                      {applying === key ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Applying…</> : <><Plus className="w-3.5 h-3.5 mr-1.5" /> Apply</>}
                    </Button>
                    <Button onClick={() => onDismiss(key)} variant="outline" className="h-10 border-slate-300 text-slate-500 hover:text-rose-600 text-xs" data-testid={`action-dismiss-${(actions || []).indexOf(action)}`}>
                      <X className="w-3.5 h-3.5 mr-1.5" /> Skip
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SuggestionTray;
